import {
  MidwayIdentityCredentialProviderCognito,
  MidwayIdentityCredentialProviderSTS,
} from '@amzn/midway-identity-credential-provider';
import {
  authUsesCognito,
  authUsesIsengard,
  getAuthConfig,
  getMidwayAuthorizedRoleArn,
  getMidwayPartition,
  getRegion,
} from './config';
import {Credentials} from 'aws-sdk/global';
import {waitForIsengardCredentials} from 'src/components/common/IsengardAuthModal';

var credentials;
var stsCredentialProvider: MidwayIdentityCredentialProviderSTS;
var user;
let isengardCredentials;

// the credentials provided by MidwayIdentityCredentialProviderSTS extend aws-sdk/client-sts/Credentials,
// rather than aws-sdk/lib/credentials/Credentials, which is the one we pass in to the client.
// this simple wrapper allows us to pass it in, so the clients can periodically refresh the crednetials automatically.
class MidwaySTSCredentials extends Credentials {
  private stsCredentialProvider: MidwayIdentityCredentialProviderSTS;
  private midwayUser: string;

  constructor(
    stsCredProvider: MidwayIdentityCredentialProviderSTS,
    accessKeyId: string,
    secretAccessKey: string,
    sessionToken: string,
    midwayUser: string,
  ) {
    super(accessKeyId, secretAccessKey, sessionToken);
    this.stsCredentialProvider = stsCredProvider;
    this.midwayUser = midwayUser;
  }

  async refresh() {
    const stsCredentials = await this.stsCredentialProvider.getCredentials();
    this.accessKeyId = stsCredentials.AccessKeyId;
    this.secretAccessKey = stsCredentials.SecretAccessKey;
    this.sessionToken = stsCredentials.SessionToken;
    this.midwayUser = stsCredentials.MidwayUser;
  }

  getMidwayUser() {
    return this.midwayUser;
  }
}

export function getMidwayCredentialProviderSTS() {
  return new MidwayIdentityCredentialProviderSTS(
      getMidwayAuthorizedRoleArn(),
      window.location.host,
      new URL(window.location.href),
      getMidwayPartition(),
      {
        maxAttempts: 3,
        region: getRegion(),
      },
  );
}

export async function initCredentials() {
  // functions in this file split into three cases: credentials provided by Isengard, Cognito, or STS
  // Cognito is used by the original stages (beta IAD, prod IAD) and STS everywhere else.
  // If STS doesn't work in a region, Isengard credentials must be used.
  // details here: https://code.amazon.com/packages/MidwayIdentityCredentialProvider/trees/mainline

  if (authUsesIsengard()) {
    console.log('Fetching credentials using Isengard');
    if (!isengardCredentials) {
      isengardCredentials = await waitForIsengardCredentials();
    }
    credentials = new Credentials(isengardCredentials.credentials);
  } else if (authUsesCognito()) {
    console.log('Fetching credentials using cognito');
    // if credentials already exist, refresh if needed, otherwise return
    if (credentials !== undefined) {
      // refresh credentials if needed
      if (credentials.needsRefresh()) {
        await credentials.refresh(() => {});
        // a recent commit broke backwards compatibility of the credential provider
        // cred.refresh() no longer returns a promise
        // https://code.amazon.com/packages/MidwayIdentityCredentialProvider/commits/2c9e7df89f4b80f350096b33a25eb7b40dee7988#
        if (typeof credentials.refreshTokenAndCredentialsPromise === 'function') {
          await credentials.refreshTokenAndCredentialsPromise();
        }
      }
      return;
    }
    // else, initialize credentials
    const config = getAuthConfig();
    credentials = new MidwayIdentityCredentialProviderCognito(
      { IdentityPoolId: config.identityPoolId },
      window.location.host,
      new URL(window.location.href),
      { region: config.region },
      getMidwayPartition(),
    );
  } else {
    console.log('Fetching credentials using iam role');
    // if credential provider doesn't exist, create it now
    if (stsCredentialProvider === undefined) {
      stsCredentialProvider = new MidwayIdentityCredentialProviderSTS(
        getMidwayAuthorizedRoleArn(),
        window.location.host,
        new URL(window.location.href),
        getMidwayPartition(),
        {
          maxAttempts: 3,
          region: getRegion(),
        },
      );
    }

    const stsCredentials = await stsCredentialProvider.getCredentials();
    credentials = new MidwaySTSCredentials(
      stsCredentialProvider,
      stsCredentials.AccessKeyId,
      stsCredentials.SecretAccessKey,
      stsCredentials.SessionToken,
      stsCredentials.MidwayUser,
    );
  }
}

export async function getCredentials() {
  await initCredentials();
  return credentials;
}

export async function getUser() {
  await initCredentials();

  if (authUsesIsengard()) {
    if (user) return user;
    user = parseUserFromAssumedARN(isengardCredentials.assumedRoleUser.arn);
    return user;
  } else if (authUsesCognito()) {
    try {
      if (user) {
        return user;
      }
      const token = credentials.params['Logins']['midway-auth.amazon.com'];
      user = parseJwt(token).sub;
    } catch (err) {
      console.log(err);
    }
    return user;
  } else {
    return credentials.getMidwayUser();
  }
}

function parseJwt(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );
  return JSON.parse(jsonPayload);
}

function parseUserFromAssumedARN(arn: string): string | undefined {
  // Example: `arn:aws:sts::182597795218:assumed-role/ReadOnly/jbcolli-Isengard` => `jbcolli`
  return arn.split('/').reverse()[0]?.split('-Isengard')[0] ?? undefined;
}
